import { useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

import { ShareDialogMode, sessionUrl } from "../share-session-dialog-constants";
import type { PinnedVersionSelection } from "../pinned-version-select";

import type { CreateDashboardSharedSessionResponse } from "@/queries/prompts/create-dashboard-shared-session";
import type { SharedSessionTarget } from "@/queries/prompts/shared-session-cache-utils";
import type { SharedSession } from "@/queries/prompts/types";
import type { DeleteSharedSessionRequest } from "@/queries/prompts/useDeleteSharedSession";
import type { UpdateSharedSessionRequest } from "@/queries/prompts/useUpdateSharedSession";
import {
  buildSharedSessionExistsQueryKey,
  buildSharedSessionsQueryKey,
  useDeleteSharedSession,
  useSharedSessionExists,
  useSharedSessions,
  useUpdateSharedSession,
} from "@/queries/prompts";

export interface ShareSessionDialogCreateContext {
  name: string;
  is_public: boolean;
  versionSelection: PinnedVersionSelection;
}

export interface UseShareSessionDialogParams<TCreateVariables> {
  queryTarget: SharedSessionTarget;
  existsQueryEnabled: boolean;
  resourceName: string;
  defaultVersionSelection: PinnedVersionSelection;
  createMutation: UseMutationResult<CreateDashboardSharedSessionResponse, Error, TCreateVariables>;
  resetCreateMutation: () => void;
  buildCreateVariables: (ctx: ShareSessionDialogCreateContext) => TCreateVariables | null;
  getDeleteVariables: (sessionId: string) => DeleteSharedSessionRequest | null;
  getUpdateVariables: (
    sessionId: string,
    fields: Partial<
      Pick<
        SharedSession,
        | "name"
        | "is_public"
        | "batch_enabled"
        | "chat_enabled"
        | "remix_enabled"
        | "label_name"
        | "version_number"
      >
    >,
  ) => UpdateSharedSessionRequest | null;
  resetExtraFormState?: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const useShareSessionDialog = <TCreateVariables>({
  queryTarget,
  existsQueryEnabled,
  resourceName,
  defaultVersionSelection,
  createMutation,
  resetCreateMutation,
  buildCreateVariables,
  getDeleteVariables,
  getUpdateVariables,
  resetExtraFormState,
  open: openProp,
  onOpenChange,
}: UseShareSessionDialogParams<TCreateVariables>) => {
  const queryClient = useQueryClient();
  const updateSharedSessionMutation = useUpdateSharedSession();
  const deleteSharedSessionMutation = useDeleteSharedSession();

  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isOpenControlled = openProp !== undefined;
  const open = isOpenControlled ? openProp : uncontrolledOpen;
  const setOpen = useCallback(
    (nextOpen: boolean) => {
      if (!isOpenControlled) {
        setUncontrolledOpen(nextOpen);
      }
      onOpenChange?.(nextOpen);
    },
    [isOpenControlled, onOpenChange],
  );
  const userChoseCreate = useRef(false);
  const [mode, setMode] = useState<ShareDialogMode>(ShareDialogMode.CREATE);
  const [name, setName] = useState(resourceName);
  const [isPublic, setIsPublic] = useState(true);
  const [versionSelection, setVersionSelection] =
    useState<PinnedVersionSelection>(defaultVersionSelection);
  const [newShareUrl, setNewShareUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setVersionSelection(defaultVersionSelection);
    }
  }, [defaultVersionSelection, open]);

  const resetForm = useCallback(() => {
    setName(resourceName);
    setIsPublic(true);
    setVersionSelection(defaultVersionSelection);
    setNewShareUrl(null);
    resetExtraFormState?.();
    resetCreateMutation();
  }, [defaultVersionSelection, resetCreateMutation, resetExtraFormState, resourceName]);

  const { data: hasExistingSessions, isLoading: isCheckingExistence } = useSharedSessionExists({
    workspaceId: queryTarget.workspaceId,
    promptId: queryTarget.promptId,
    workflowId: queryTarget.workflowId,
    skillCollectionId: queryTarget.skillCollectionId,
    enabled: existsQueryEnabled,
  });

  const { data: sessions, isLoading: isLoadingSessions } = useSharedSessions({
    workspaceId: queryTarget.workspaceId,
    promptId: queryTarget.promptId,
    workflowId: queryTarget.workflowId,
    skillCollectionId: queryTarget.skillCollectionId,
    enabled: open && mode === ShareDialogMode.MANAGE,
  });

  useEffect(() => {
    if (mode === ShareDialogMode.MANAGE && sessions !== undefined && sessions.length === 0) {
      userChoseCreate.current = true;
      resetForm();
      setMode(ShareDialogMode.CREATE);
    }
    // resetForm is stable (useCallback) — safe to omit from deps via lint rule
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions, mode]);

  useEffect(() => {
    if (
      open &&
      mode === ShareDialogMode.CREATE &&
      hasExistingSessions === true &&
      !userChoseCreate.current
    ) {
      setMode(ShareDialogMode.MANAGE);
    }
  }, [hasExistingSessions, mode, open]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      if (nextOpen) {
        resetForm();
        userChoseCreate.current = false;
        setMode(hasExistingSessions === true ? ShareDialogMode.MANAGE : ShareDialogMode.CREATE);
      }
    },
    [hasExistingSessions, resetForm, setOpen],
  );

  const handleCreate = useCallback(() => {
    const variables = buildCreateVariables({
      name,
      is_public: isPublic,
      versionSelection,
    });
    if (!variables) {
      return;
    }

    createMutation.mutate(variables, {
      onSuccess: data => {
        setNewShareUrl(sessionUrl(data.shared_session.share_token));
        userChoseCreate.current = true;
        setMode(ShareDialogMode.CREATED);
      },
    });
  }, [buildCreateVariables, createMutation, isPublic, name, versionSelection]);

  const handleDeleteSession = useCallback(
    (sessionId: string) => {
      const deleteVariables = getDeleteVariables(sessionId);
      if (!deleteVariables) {
        return;
      }

      const sessionsKey = buildSharedSessionsQueryKey(queryTarget);
      const existsKey = buildSharedSessionExistsQueryKey(queryTarget);
      const snapshot = queryClient.getQueryData<SharedSession[]>(sessionsKey);

      queryClient.setQueryData<SharedSession[]>(sessionsKey, old =>
        old ? old.filter(s => s.id !== sessionId) : [],
      );

      const remaining = queryClient.getQueryData<SharedSession[]>(sessionsKey) ?? [];

      if (remaining.length === 0) {
        queryClient.setQueryData(existsKey, false);
        userChoseCreate.current = true;
        resetForm();
        setMode(ShareDialogMode.CREATE);
      }

      deleteSharedSessionMutation.mutate(deleteVariables, {
        onError: () => {
          queryClient.setQueryData(sessionsKey, snapshot);
          if ((snapshot ?? []).length > 0) {
            queryClient.setQueryData(existsKey, true);
          }
          if (remaining.length === 0) {
            userChoseCreate.current = false;
            setMode(ShareDialogMode.MANAGE);
          }
        },
        onSettled: () => {
          queryClient.invalidateQueries({ queryKey: sessionsKey });
          queryClient.invalidateQueries({ queryKey: existsKey });
        },
      });
    },
    [deleteSharedSessionMutation, getDeleteVariables, queryClient, queryTarget, resetForm],
  );

  const handleUpdateSession = useCallback(
    (
      sessionId: string,
      fields: Partial<
        Pick<
          SharedSession,
          | "name"
          | "is_public"
          | "batch_enabled"
          | "chat_enabled"
          | "remix_enabled"
          | "label_name"
          | "version_number"
        >
      >,
    ) => {
      const variables = getUpdateVariables(sessionId, fields);
      if (!variables) {
        return;
      }
      updateSharedSessionMutation.mutate(variables);
    },
    [getUpdateVariables, updateSharedSessionMutation],
  );

  const handleNavigateToManage = useCallback(() => {
    userChoseCreate.current = false;
    setMode(ShareDialogMode.MANAGE);
  }, []);

  const isCreateDisabled = !name.trim() || createMutation.isPending;
  const hasSessionsToManage =
    sessions !== undefined ? sessions.length > 0 : hasExistingSessions === true;

  return {
    open,
    mode,
    setMode,
    userChoseCreate,
    name,
    setName,
    isPublic,
    setIsPublic,
    versionSelection,
    setVersionSelection,
    newShareUrl,
    hasExistingSessions,
    isCheckingExistence,
    sessions,
    isLoadingSessions,
    resetForm,
    handleOpenChange,
    handleCreate,
    handleDeleteSession,
    handleUpdateSession,
    handleNavigateToManage,
    isCreateDisabled,
    hasSessionsToManage,
  };
};
